import React, { useState, useEffect } from 'react';

/**
 * 🔊 Aussprache-Kontrolle einer Video-Idee (Posting-Karte).
 *
 * Anlass 08/2026: die Stimme sagte „Kai" statt „KI" — im Satz repariert Whisper
 * so etwas still, isoliert nicht. Der Video-Dienst hört deshalb Risikowörter
 * (Kürzel, Eigennamen, Anglizismen) EINZELN an und hört die fertige Aufnahme
 * gegen. Hier sieht man die Befunde, pflegt Umschriften (nur Sprachausgabe —
 * der Untertitel bleibt das Original) und rendert damit neu.
 *
 * Quellen: repurpose.pronounce (Prüfung ohne Render), repurpose.video_pronunciation
 * (Vorab-Hörprobe + Rückhören + Take-Hörprobe des letzten Renders), repurpose.lexicon.
 */

const KIND_LABEL = { 'kürzel': 'Kürzel', eigenname: 'Eigenname', anglizismus: 'Anglizismus' };
const VERDICT = {
  ok:         { icon: '✓', cls: 'pron-ok',   title: 'in allen Läufen richtig gehört' },
  unsicher:   { icon: '⚠', cls: 'pron-warn', title: 'nur in einem Teil der Läufe richtig — die Stimme ist nicht deterministisch' },
  abweichend: { icon: '✗', cls: 'pron-bad',  title: 'in keinem Lauf richtig gehört — Umschrift eintragen' },
  unklar:     { icon: '·', cls: 'pron-muted', title: 'zu kurz für ein Urteil' },
};
const fmtT = iso => iso ? new Date(iso).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '';

export default function PronounceBlock({ idea, authToken, pushToast, onChanged, engineLocal, busy, setBusy }) {
  const rp = idea.repurpose || {};
  const check = rp.pronounce || null;                 // {status, risks, took, checked_at, error}
  const render = rp.video_pronunciation || null;      // {probe, issues, snippets, takes, ok, checked_at}
  const savedLex = rp.lexicon || {};
  const [lex, setLex] = useState(savedLex);
  const [open, setOpen] = useState(false);
  const running = check?.status === 'running';
  const rendering = idea.revid_status === 'rendering';
  const auth = { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' };

  // Gespeicherte Umschriften nachziehen, wenn die Idee neu geladen wurde
  const savedKey = JSON.stringify(savedLex);
  useEffect(() => { setLex(JSON.parse(savedKey)); }, [savedKey, idea.id]);

  // Neueste Hörprobe: explizite Prüfung oder die aus dem letzten Render — was jünger ist
  const probeRows = (() => {
    const a = check?.status === 'done' ? { at: check.checked_at, rows: check.risks || [], src: 'Prüfung' } : null;
    const b = render?.probe?.length ? { at: render.checked_at, rows: render.probe, src: `Render` } : null;
    if (a && b) return (a.at || '') >= (b.at || '') ? a : b;
    return a || b;
  })();
  const bad = (probeRows?.rows || []).filter(r => r.verdict !== 'ok').length;
  const renderIssues = render?.issues || [];
  const renderSnips = (render?.snippets || []).filter(s => s.verdict === 'abweichend');
  const hasFindings = bad > 0 || renderIssues.length > 0 || renderSnips.length > 0;
  const dirty = JSON.stringify(lex) !== savedKey;

  const startCheck = async () => {
    setBusy(`pron-${idea.id}`);
    const r = await fetch(`/api/ideas/${idea.id}/video/pronounce`, { method: 'POST', headers: auth, body: '{}' }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && r.status === 202) { pushToast('🔊 Aussprache wird geprüft — Risikowörter werden einzeln angehört (20–60 s)', 'info'); onChanged(); }
    else pushToast(d.error || 'Prüfung nicht möglich', r && (r.status === 409 || r.status === 400 || r.status === 503) ? 'info' : 'error');
    setBusy('');
  };

  const saveLex = async () => {
    setBusy(`lex-${idea.id}`);
    const r = await fetch(`/api/ideas/${idea.id}/video/lexicon`, { method: 'PUT', headers: auth, body: JSON.stringify({ lexicon: lex }) }).catch(() => null);
    const d = r ? await r.json().catch(() => ({})) : {};
    if (r && r.ok) { pushToast(`Umschriften gespeichert (${Object.keys(d.lexicon || {}).length}) — wirken beim nächsten Render`, 'success'); onChanged(); }
    else pushToast(d.error || 'Speichern fehlgeschlagen', 'error');
    setBusy('');
  };

  // Chip-Text für die zugeklappte Ansicht
  let chip, chipCls = 'pron-chip';
  if (running) { chip = '⏳ prüft…'; }
  else if (probeRows || render) {
    if (!hasFindings) { chip = '✓ sauber'; chipCls += ' pron-chip-ok'; }
    else { chip = `⚠ ${bad + renderIssues.length + renderSnips.length} auffällig`; chipCls += ' pron-chip-warn'; }
  } else chip = 'ungeprüft';

  return (
    <div className="pron-block">
      <div className="pron-head">
        <button className="pron-toggle" onClick={() => setOpen(o => !o)} title="Aussprache-Befunde ein-/ausklappen">
          {open ? '▾' : '▸'} 🔊 Aussprache <span className={chipCls}>{chip}</span>
          {render?.takes > 1 && <span className="pron-take" title="Die Stimme wurde zweimal aufgenommen, der sauberere Take ist im Video">Take {render.takes}</span>}
        </button>
        <div className="pron-actions">
          <button className="btn-ghost-sm" disabled={!!busy || running || rendering || !engineLocal}
            onClick={startCheck}
            title={!engineLocal ? 'Lokale Video-Pipeline nicht erreichbar' : rendering ? 'Kommt mit dem laufenden Render' : 'Risikowörter jetzt einzeln anhören (ohne Render, 20–60 s)'}>
            {running ? '⏳' : '🔊'} Prüfen
          </button>
        </div>
      </div>

      {open && (
        <div className="pron-body">
          {check?.status === 'error' && <p className="pron-error">Prüfung fehlgeschlagen: {check.error}</p>}
          {!probeRows && !render && !running && (
            <p className="posting-hint">Noch nicht geprüft. „🔊 Prüfen" hört Kürzel, Eigennamen und Anglizismen des Skripts einzeln an; jeder Render prüft ohnehin Kürzel + Eigennamen und hört die Aufnahme gegen.</p>
          )}

          {probeRows && (
            <>
              <div className="pron-src">Hörprobe (isoliert, {probeRows.src}{probeRows.at ? `, ${fmtT(probeRows.at)}` : ''}) — Wort einzeln gesprochen, von Whisper zurückgehört</div>
              <div className="pron-table" role="table">
                <div className="pron-row pron-row-head" role="row"><span>Wort</span><span>gesprochen als</span><span>gehört</span><span>Umschrift (nur Ton)</span></div>
                {probeRows.rows.map(r => {
                  const v = VERDICT[r.verdict] || VERDICT.unklar;
                  return (
                    <div key={r.word} className="pron-row" role="row">
                      <span className="pron-word" title={KIND_LABEL[r.kind] || r.kind}>
                        <span className={`pron-verdict ${v.cls}`} title={v.title}>{v.icon}</span> {r.word}
                        <span className="pron-kind">{KIND_LABEL[r.kind] || r.kind}</span>
                      </span>
                      <span className="pron-spoken" title="So bekommt die Stimme das Wort (nach Lexikon + Regeln)">{r.spoken !== r.word ? r.spoken : '—'}</span>
                      <span className="pron-heard" title={`${r.hits}/${r.repeats} Läufe richtig`}>{(r.heard || []).join(' · ') || '—'} <em>{r.hits}/{r.repeats}</em></span>
                      <span>
                        <input className="pron-input" value={lex[r.word] ?? ''} placeholder={r.verdict === 'ok' ? '' : 'z. B. Ka Ih'}
                          maxLength={40} onChange={e => setLex({ ...lex, [r.word]: e.target.value })}
                          title="Wie die Stimme das Wort sprechen soll — Buchstaben, Ziffern, Bindestrich, Leerzeichen. Der Untertitel bleibt das Original." />
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {render && (renderIssues.length > 0 || renderSnips.length > 0 || render.checked_at) && (
            <div className="pron-render">
              <div className="pron-src">Aufnahme des letzten Renders ({fmtT(render.checked_at)}{render.takes ? `, Take ${render.takes}` : ''}) — Skript gegen das Gehörte</div>
              {renderIssues.length === 0 && renderSnips.length === 0
                ? <p className="pron-clean">✓ Rückhören ohne Abweichung — die Stimme sagt das Skript.</p>
                : (
                  <ul className="pron-issues">
                    {renderSnips.map((s, k) => <li key={`s${k}`}><span className="pron-verdict pron-bad">✗</span> Risikowort <strong>{s.word}</strong> bei {s.at}s → gehört „{s.heard}"</li>)}
                    {renderIssues.map((it, k) => <li key={`i${k}`}><span className="pron-verdict pron-warn">⚠</span> „{it.want}" → gehört „{it.heard}"</li>)}
                  </ul>
                )}
            </div>
          )}

          {(probeRows || Object.keys(lex).length > 0) && (
            <div className="pron-foot">
              <button className="btn-outline" disabled={!!busy || !dirty} onClick={saveLex}
                title="Umschriften an der Idee speichern — sie gelten für den nächsten Render und die nächste Prüfung">💾 Umschriften speichern</button>
              <span className="posting-hint">Danach „🔊 Prüfen" (hört die Umschrift an) oder „🎬 Neu rendern". Regeln: Buchstaben-Kürzel als Namen schreiben (KI → „Ka Ih"), Anglizismen lautschriftlich (Reward → „Riwoard").</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
